import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";

interface StripeResult {
  ok: boolean;
  seen: number;
  upserts: number;
  commissionsCreated: number;
  errors: string[];
  error?: string;
  hasMore: boolean;
  nextCursor: string | null;
}

interface StripeRunningTotals {
  chunks: number;
  seen: number;
  upserts: number;
  commissionsCreated: number;
}
interface HubspotResult {
  ok: boolean;
  seen: number;
  upserts: number;
  stageChanges: number;
  ownersCached: number;
  stagesCached: number;
  errors: string[];
  error?: string;
}

export const Route = createFileRoute("/admin/sync")({
  component: SyncPage,
});

function SyncPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [since, setSince] = useState("2024-01-01");
  const [stripeRes, setStripeRes] = useState<StripeResult | null>(null);
  const [stripeCursor, setStripeCursor] = useState<string | null>(null);
  const [stripeTotals, setStripeTotals] = useState<StripeRunningTotals | null>(null);
  const [hubspotRes, setHubspotRes] = useState<HubspotResult | null>(null);

  // `cursor` is undefined for a fresh run, or the previous chunk's nextCursor
  // when the admin clicks "Continue backfill".
  const syncStripe = useMutation({
    mutationFn: (cursor?: string) =>
      api.post<StripeResult>("/api/sync/stripe", undefined, {
        searchParams: { since, cursor },
      }),
    onSuccess: (res, cursor) => {
      setStripeRes(res);
      setStripeCursor(res.hasMore ? res.nextCursor : null);
      // Accumulate across chunks; a fresh run (no cursor) resets the totals.
      setStripeTotals((prev) => {
        const base = cursor && prev ? prev : { chunks: 0, seen: 0, upserts: 0, commissionsCreated: 0 };
        return {
          chunks: base.chunks + 1,
          seen: base.seen + res.seen,
          upserts: base.upserts + res.upserts,
          commissionsCreated: base.commissionsCreated + res.commissionsCreated,
        };
      });
      if (res.ok) {
        const summary = `${res.upserts} invoice${res.upserts === 1 ? "" : "s"} upserted, ${res.commissionsCreated} commission${res.commissionsCreated === 1 ? "" : "s"} created.`;
        toast.ok(
          res.hasMore
            ? `Stripe backfill chunk done: ${summary} More invoices remain — click "Continue backfill".`
            : `Stripe backfill: ${summary}`,
          8000,
        );
      } else {
        toast.err(res.error ?? "Stripe backfill failed.");
      }
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        typeof err.body === "object" &&
        err.body !== null &&
        (err.body as { error?: unknown }).error === "sync_already_running"
      ) {
        toast.err("A sync is already running — wait for it to finish, then try again.");
        return;
      }
      toast.err(err instanceof ApiError ? err.message : "Stripe sync failed.");
    },
  });

  const syncHubspot = useMutation({
    mutationFn: () => api.post<HubspotResult>("/api/sync/hubspot"),
    onSuccess: (res) => {
      setHubspotRes(res);
      if (res.ok) {
        toast.ok(
          `HubSpot backfill: ${res.upserts} deal${res.upserts === 1 ? "" : "s"} upserted, ${res.stageChanges} stage changes logged, ${res.ownersCached} owners + ${res.stagesCached} stages cached.`,
          8000,
        );
      } else {
        toast.err(res.error ?? "HubSpot backfill failed.");
      }
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "HubSpot sync failed."),
  });

  async function onSyncStripe() {
    const ok = await confirm({
      title: "Stripe backfill",
      prompt: `Pull every invoice since ${since} from Stripe and upsert into Supabase. invoice.paid invoices that link to deals will produce commissions. Heavy operation — may take a minute.`,
      confirmLabel: "Run backfill",
    });
    if (ok) {
      // Fresh run: drop any cursor + totals from a previous chunked run.
      setStripeCursor(null);
      setStripeTotals(null);
      syncStripe.mutate(undefined);
    }
  }
  function onContinueStripe() {
    if (stripeCursor) syncStripe.mutate(stripeCursor);
  }
  async function onSyncHubspot() {
    const ok = await confirm({
      title: "HubSpot backfill",
      prompt: "Pull every closed-won deal from HubSpot, refresh owners + stages caches, and enrich deals with company / contact info. Heavy operation.",
      confirmLabel: "Run backfill",
    });
    if (ok) syncHubspot.mutate();
  }

  return (
    <>
      <h1>Backfill / sync</h1>

      <Panel title="Stripe invoices">
        <p className="muted">
          Re-pull every Stripe invoice since the date below and run them through the same commissionable-decision +
          commission-creation path as live webhooks. Existing rows update in place; new <code>invoice.paid</code> events
          may produce new commissions.
        </p>
        <div className="inline" style={{ gap: 12, marginTop: 12 }}>
          <label>
            Since
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} style={{ marginLeft: 8 }} />
          </label>
          <button className="primary" onClick={onSyncStripe} disabled={syncStripe.isPending}>
            {syncStripe.isPending ? "Running…" : "Run Stripe backfill"}
          </button>
          {stripeCursor && !syncStripe.isPending && (
            <button className="primary" onClick={onContinueStripe}>
              Continue backfill →
            </button>
          )}
        </div>
        {stripeRes?.hasMore && stripeCursor && (
          <p className="muted" style={{ marginTop: 12, marginBottom: 0, fontSize: 12.5 }}>
            The run stopped early to stay within Worker limits — more invoices remain after cursor{" "}
            <code>{stripeCursor}</code>. Click <strong>Continue backfill</strong> to process the next chunk.
          </p>
        )}
        {stripeTotals && (
          <p className="muted" style={{ marginTop: 12, marginBottom: 0, fontSize: 12.5 }}>
            Running total across {stripeTotals.chunks} chunk{stripeTotals.chunks === 1 ? "" : "s"}:{" "}
            {stripeTotals.seen} seen, {stripeTotals.upserts} upserted, {stripeTotals.commissionsCreated}{" "}
            commission{stripeTotals.commissionsCreated === 1 ? "" : "s"} created.
          </p>
        )}
        {stripeRes && (
          <pre style={{ marginTop: 14, fontSize: 11.5, background: "var(--gray50)", padding: 12, borderRadius: 6, overflow: "auto" }}>
            {JSON.stringify(stripeRes, null, 2)}
          </pre>
        )}
      </Panel>

      <Panel title="HubSpot closed-won deals">
        <p className="muted">
          Pulls every <code>hs_is_closed_won = true</code> deal closed since {new Date("2024-01-01").toISOString().slice(0, 10)},
          refreshes the owners + stages caches, and enriches each deal with associated company + primary contact (used
          downstream by the customer auto-link).
        </p>
        <div className="inline" style={{ gap: 12, marginTop: 12 }}>
          <button className="primary" onClick={onSyncHubspot} disabled={syncHubspot.isPending}>
            {syncHubspot.isPending ? "Running…" : "Run HubSpot backfill"}
          </button>
        </div>
        {hubspotRes && (
          <pre style={{ marginTop: 14, fontSize: 11.5, background: "var(--gray50)", padding: 12, borderRadius: 6, overflow: "auto" }}>
            {JSON.stringify(hubspotRes, null, 2)}
          </pre>
        )}
      </Panel>

      <Panel variant="warn">
        <p style={{ margin: 0, fontSize: 12.5 }}>
          Both backfills are idempotent and safe to re-run. The HubSpot one is the heavier of the two on the API side —
          it can hit 10s of seconds even on a small portal.
        </p>
      </Panel>
    </>
  );
}
